import { Button } from "@material-ui/core";
import { CircularProgress, TextField } from "@material-ui/core";
import { Snackbar } from "@material-ui/core";
import { Grid } from "@material-ui/core";
import { FormControlLabel } from "@material-ui/core";
import { Checkbox } from "@material-ui/core";
import { Dialog } from "@material-ui/core";
import { DialogActions } from "@material-ui/core";
import { DialogContent } from "@material-ui/core";
import { DialogContentText } from "@material-ui/core";
import { DialogTitle } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import MesfModal from "../../../../../components/modals/modal.mesf";
import { LazyLoading } from "../../../../../controls/LazyLoading/LazyLoading";
import { Alert } from "../../../../../controls/alert";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { User } from "../../models/User";
import { deleteUser, getDataUser } from "../../repositories/UsersRepository";
import { AuthTypeSelector } from "../common/AuthTypeSelector";
import { ProfilesPicker } from "../common/ProfilesPicker";

type Props = {
	userId: number | null;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const DeleteUser = ({ userId, show, onHide }: Props) => {
	const [open, setOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [deleteAlert, setDeleteAlert] = useState(false);
	const [error, setError] = useState("");
	const [profileError, setprofileError] = useState("");

	const { register, control, setValue } = useForm<User>({
		defaultValues: INITIAL_VALUES,
	});

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
		setDeleteAlert(false);
	};

	const onSubmit = async (userSelected: number | null) => {
		setIsSubmitLoading(true);
		const deleteUserResp = await deleteUser(userSelected as number);
		if (deleteUserResp.ok) {
			setOpen(true);
			onHide(true);
			setDeleteAlert(false);
			setIsSubmitLoading(false);
		} else {
			setError(deleteUserResp.message);
			setIsSubmitLoading(false);
			setDeleteAlert(false);
		}
	};

	useEffect(() => {
		if (show) {
			setIsSubmitLoading(false);
			(async () => {
				setIsLoading(true);
				const userResponse = await getDataUser(userId);
				if (userResponse.ok) {
					const userSelected = userResponse.data.tables[0].rows[0];
					if (userSelected) {
						const profilesIds = userSelected!.ProfilesId?.split(",") || [];

						setValue("UserId", userSelected!.UserId);
						setValue("UserName", userSelected!.UserName);
						setValue("FirstName", userSelected!.FirstName);
						setValue("LastName", userSelected!.LastName);
						setValue("AuthTypeId", userSelected!.AuthTypeId);
						setValue("IsActive", userSelected!.IsActive);
						setValue("LastLogin", userSelected!.LastLogin);
						setValue(
							"ProfilesId",
							profilesIds.map((pId: string) => parseInt(pId)),
						);
						setIsLoading(false);
					}
				} else setError(userResponse.message);
			})();
		}
	}, [show]);

	return (
		<>
			<MesfModal
				title="DELETE USER"
				open={show}
				handleClose={() => onHide(false)}
				id="delete-user-modal"
				maxWidth="md"
			>
				<form>
					<MesfModal.Content style={{ padding: "15px 30px" }}>
						{isLoading && (
							<Grid
								container
								justifyContent="center"
								alignItems="center"
								style={{ minHeight: "300px" }}
							>
								<LazyLoading />
							</Grid>
						)}
						{!isLoading && (
							<>
								<Grid container spacing={1}>
									<Grid item xs={12}>
										<TextField
											{...register("UserName", { required: true })}
											label="Username"
											disabled
											variant="outlined"
											fullWidth
											margin="dense"
											autoComplete="off"
										/>
									</Grid>
									<Grid item md={6} xs={12}>
										<TextField
											{...register("FirstName", { required: true })}
											label="First name"
											variant="outlined"
											disabled
											fullWidth
											margin="dense"
											autoComplete="off"
										/>
									</Grid>
									<Grid item md={6} xs={12}>
										<TextField
											{...register("LastName", { required: true })}
											label="Last name"
											variant="outlined"
											disabled
											fullWidth
											margin="dense"
											autoComplete="off"
										/>
									</Grid>
									<Grid item container xs={12} spacing={3}>
										<Grid item xs={12} md={6}>
											<Controller
												name="ProfilesId"
												control={control}
												rules={{ required: true }}
												render={({ field: { onChange, value } }) => (
													<ProfilesPicker
														onChange={onChange}
														value={value as number[]}
														userIdForDelete={null}
														setprofileError={setprofileError}
														disabled={true}
													/>
												)}
											/>
										</Grid>
										<Grid item xs={12} md={6}>
											<Controller
												name="AuthTypeId"
												control={control}
												render={({ field: { value, onChange } }) => (
													<AuthTypeSelector
														value={value}
														onChange={onChange}
														disabled={true}
													/>
												)}
											/>
										</Grid>
										<Grid item xs={12} md={6}>
											<Controller
												name="IsActive"
												control={control}
												render={({ field }) => (
													<FormControlLabel
														control={
															<Checkbox
																checked={field.value}
																onChange={field.onChange}
																name="IsActive"
																color="primary"
																disabled
															/>
														}
														label="Active"
													/>
												)}
											/>
										</Grid>
									</Grid>
								</Grid>
							</>
						)}
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid item md={3} xs={12} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="default"
									onClick={() => onHide(false)}
								>
									CANCEL
								</Button>
							</Grid>
							<Grid item md={3} xs={12} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="secondary"
									onClick={() => setDeleteAlert(true)}
								>
									DELETE
								</Button>
							</Grid>
						</Grid>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<Snackbar open={open} autoHideDuration={2500} onClose={handleClose}>
				<Alert severity="warning" onClose={handleClose}>
					The user was deleted successfully
				</Alert>
			</Snackbar>
			<MesfModal
				open={deleteAlert}
				title="WARNING"
				handleClose={() => handleClose()}
				id="responsive-dialog-title"
			>
				<MesfModal.Content>
					Be careful, once deleted you will not be able to recover this user,
					are you sure you want to delete it?
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "16px 24px" }}>
					<Button
						onClick={() => setDeleteAlert(false)}
						variant="contained"
						color="default"
					>
						CANCEL
					</Button>
					<Button
						startIcon={isSubmitLoading && <CircularProgress size="1rem" />}
						variant="contained"
						color="secondary"
						onClick={() => onSubmit(userId)}
					>
						Delete
					</Button>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Deleting User"
			/>
			<ErrorModal
				error={profileError}
				onHide={() => setprofileError("")}
				title="Error Choosing Profile"
			/>
		</>
	);
};
